"use client";

import LiveBadgeButton from "@/components/ui/buttons/badge-live";
import {
	NavigationMenu,
	NavigationMenuContent,
	NavigationMenuItem,
	NavigationMenuLink,
	NavigationMenuList,
	NavigationMenuTrigger,
	navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import { TooltipContent } from "@/components/ui/tooltip-content";
import { cn } from "@/lib/utils";
import {
	ActivityIcon,
	BuildingIcon,
	DollarSignIcon,
	HistoryIcon,
	InfoIcon,
	type LucideIcon,
	MessageCircleQuestionMark,
	PhoneCallIcon,
	PhoneIcon,
	PlayCircleIcon,
	SparkleIcon,
	UsersIcon,
} from "lucide-react";
import Link from "next/link";
import type * as React from "react";
import { useEffect } from "react";
import { GiModernCity } from "react-icons/gi";
import type { IconType } from "react-icons/lib";
import { MdCardTravel, MdEmojiEvents } from "react-icons/md";

export type Status = "beta" | "coming-soon" | "live";

export const getStatusBadge = (status: Status) => {
	switch (status) {
		case "beta":
			return "Beta";
		case "coming-soon":
			return "Coming Soon";
		case "live":
			return "Live";
	}
};

export const menuItems: MenuItem[] = [
	{
		label: "Live Maps",
		href: "/products",
		description: "More about our platform.",
		showIcon: true,
		image: "/images/features/pins.webp",
		status: "live",
		items: [
			{
				label: "Events Map",
				description: "Events for local events and activities.",
				status: "live",
				featured: true,
				showLiveIcon: true,
				icon: MdEmojiEvents,
				href: "/products/use-case/events-map",
			},
			{
				label: "City Map",
				description: "Live maps for cities.",
				status: "live",
				showLiveIcon: true,
				href: "/products/use-case/city-map",
				icon: GiModernCity,
			},
			{
				label: "Travel Map",
				description: "Tourism for local events and activities.",
				status: "live",
				showLiveIcon: true,
				icon: MdCardTravel,
				href: "/products/use-case/travel-map",
			},
			{
				label: "Community Map",
				description: "Live maps for communities.",
				status: "live",
				showLiveIcon: true,
				href: "/products/use-case/community-map",
				icon: UsersIcon,
			},
		],
		callsToAction: [
			{
				label: "Watch demo",
				href: "/products/demo",
				icon: PlayCircleIcon,
			},
			{
				label: "Contact sales",
				href: "/contact",
				icon: PhoneIcon,
			},
		],
	},
	{
		label: "Solutions",
		href: "/",
		description: "",
		showIcon: true,
		icon: SparkleIcon,
		className: "lg:hidden",
		items: [
			{
				label: "Community",
				description: "Solving community challenges.",
				href: "/community",
				icon: UsersIcon,
				status: "live",
			},
			{
				label: "Enterprise",
				description: "Contact us for enterprise solutions.",
				href: "/enterprise",
				icon: BuildingIcon,
				status: "live",
			},
			{
				label: "Pricing",
				description: "Our latest pricing plans and details.",
				href: "/pricing",
				icon: DollarSignIcon,
				status: "live",
			},
		],
	},
	{
		label: "Community",
		description: "Solving community challenges.",
		href: "/community",
		className: "hidden lg:block",
	},
	// {
	// 	label: "Enterprise",
	// 	description: "Contact us for enterprise solutions",
	// 	href: "/enterprise",
	// 	className: "hidden lg:block",
	// },
	// {
	// 	label: "Pricing",
	// 	description: "Our latest pricing plans and details",
	// 	href: "/pricing",
	// 	className: "hidden lg:block",
	// },
	{
		label: "Blog",
		description: "Our latest blog posts and updates",
		href: "/blog",
		className: "hidden lg:block",
	},
	{
		label: "Resources",
		href: "/help",
		description: "Resources for helping you get started",
		showIcon: true,
		icon: SparkleIcon,
		items: [
			// {
			// 	label: "Blog",
			// 	description: "Read our latest blog posts.",
			// 	href: "/blog",
			// 	icon: BookOpenIcon,
			// 	status: "live",
			// 	featured: true,
			// },
			{
				label: "Help Center",
				description: "Let us help you.",
				href: "/help",
				icon: MessageCircleQuestionMark,
				status: "live",
				featured: true,
			},
			{
				label: "About",
				description: "Learn more about us.",
				href: "/about",
				icon: InfoIcon,
				status: "live",
			},
			{
				label: "Contact",
				description: "Contact us for support.",
				href: "/contact",
				icon: PhoneCallIcon,
				status: "live",
			},
		],
		callsToAction: [
			{
				label: "Status",
				href: "/status",
				icon: ActivityIcon,
			},
			{
				label: "Changelog",
				href: "/changelog",
				icon: HistoryIcon,
			},
		],
	},
];

export interface MenuItem {
	label: string;
	href: string;
	status?: Status;
	description?: string;
	featured?: boolean;
	showIcon?: boolean;
	showLiveIcon?: boolean;
	icon?: LucideIcon | IconType;
	image?: string;
	items?: MenuItem[];
	callsToAction?: MenuItem[];
	className?: string;
}

export function HeaderNavigationMenu() {
	const handleHashClick = (
		e: React.MouseEvent<HTMLAnchorElement>,
		href: string,
	) => {
		if (href.startsWith("/#")) {
			e.preventDefault();
			const hash = href.slice(2);
			const element = document.getElementById(hash);
			if (element) {
				const elementPosition =
					element.getBoundingClientRect().top + window.pageYOffset;
				const offsetPosition = elementPosition - 80;

				window.scrollTo({
					top: offsetPosition,
					behavior: "smooth",
				});
			}
		}
	};

	// Handle hash on page load
	useEffect(() => {
		const hash = window.location.hash.slice(1);
		if (hash) {
			const element = document.getElementById(hash);
			if (element) {
				setTimeout(() => {
					const elementPosition =
						element.getBoundingClientRect().top + window.pageYOffset;
					const offsetPosition = elementPosition - 80;

					window.scrollTo({
						top: offsetPosition,
						behavior: "smooth",
					});
				}, 100);
			}
		}
	}, []);

	return (
		<NavigationMenu className="pt-1 hidden md:block items-end">
			<NavigationMenuList className="flex-wrap p-2">
				{menuItems.map((item) => (
					<NavigationMenuItem key={item.label}>
						{!item.items ? (
							<NavigationMenuLink
								asChild
								className={cn(
									`${navigationMenuTriggerStyle()}`,
									"hidden lg:block ml-1 p-1 lg:p-2 hover:font-semibold text-primary bg-transparent",
									item.className,
								)}
							>
								<Link
									href={item.href}
									onClick={(e) => handleHashClick(e, item.href)}
								>
									{item.label}
								</Link>
							</NavigationMenuLink>
						) : (
							<>
								<NavigationMenuTrigger
									className={cn(
										"ml-1 hover:font-semibold text-primary p-1 lg:p-2 bg-transparent",
										item.className,
									)}
								>
									{item.label}
								</NavigationMenuTrigger>
								<NavigationMenuContent className="">
									<ul className="p-2 grid gap-2 md:w-[400px] lg:w-[500px] lg:grid-cols-[.75fr_1fr]">
										{item.items?.map((subItem) =>
											subItem.featured ? (
												// Featured item with larger card
												<li className="row-span-3" key={subItem.label}>
													<NavigationMenuLink
														asChild
														className="p-3 rounded-lg transition-all border border-primary/5"
													>
														<Link
															className={cn(
																"z-[20] flex h-full w-full flex-col justify-end rounded-md bg-primary/5",
																"bg-cover no-underline outline-hidden",
																"transition-all duration-200 select-none focus:shadow-md",
															)}
															style={{
																backgroundImage: item.image
																	? `url(${item.image})`
																	: "none",
															}}
															href={subItem.href}
															onClick={(e) => handleHashClick(e, subItem.href)}
														>
															{item.showIcon && subItem.icon && (
																<subItem.icon className="mb-2 size-6" />
															)}
															<div className="w-full flex flex-row items-center justify-between mb-2 text-base font-medium sm:mt-4">
																{subItem.label}
																{/* {subItem.showLiveIcon && subItem.status === "live" && (
																	<LiveBadgeButton />
																)} */}
															</div>
															<p className="text-muted-foreground text-sm leading-tight">
																{subItem.description}
															</p>
														</Link>
													</NavigationMenuLink>
												</li>
											) : (
												// Regular item
												<ListItem
													key={subItem.label}
													href={subItem.href}
													title={subItem.label}
													status={subItem.status}
													className="p-3 rounded-lg transition-all hover:bg-primary/10"
													icon={item.showIcon ? subItem.icon : undefined}
												>
													<div className="sr-only text-primary/50 line-clamp-2 text-[10px] leading-snug text-nowrap">
														{subItem.description}
													</div>
													{subItem.showLiveIcon &&
														subItem.status === "live" && <LiveBadgeButton />}
												</ListItem>
											),
										)}
									</ul>
									<ul className="grid divide-x divide-primary/10 border-t border-primary/5 grid-cols-2 w-full bg-primary/10">
										{item.callsToAction?.map((cta) => (
											<ListItem
												key={cta.label}
												href={cta.href}
												// title={cta.label}
												className="px-4 pb-3 pt-1 transition-all hover:bg-primary/10"
											>
												<div className="flex items-center justify-center gap-1">
													{cta.icon && <cta.icon className="size-5" />}
													<span className="text-sm">{cta.label}</span>
												</div>
											</ListItem>
										))}
									</ul>
								</NavigationMenuContent>
							</>
						)}
					</NavigationMenuItem>
				))}
			</NavigationMenuList>
		</NavigationMenu>
	);
}

function ListItem({
	icon,
	title,
	href,
	status,
	children,
	...props
}: React.ComponentPropsWithoutRef<"li"> & {
	href: string;
	status?: Status;
	icon?: LucideIcon | IconType;
}) {
	const Icon = icon;
	return (
		<li {...props}>
			<NavigationMenuLink asChild>
				{status === "coming-soon" ? (
					<div className="flex flex-row gap-2 items-center transition-all">
						{Icon && <Icon className="size-6 text-primary/50" />}
						<div className="text-primary/50 text-sm leading-none font-medium text-nowrap">
							{title}
						</div>
						{children}
						<div className="inline-flex items-center justify-center">
							<TooltipContent content="Coming soon">
								<InfoIcon className="inline h-3 w-3 text-primary/30" />
							</TooltipContent>
						</div>
					</div>
				) : (
					<Link
						href={href}
						className="flex flex-row gap-2 items-center transition-all cursor-pointer"
					>
						{Icon && <Icon className="size-6" />}
						<div className="mt-1 w-full flex flex-row gap-1 justify-between items-center">
							{title && (
								<div className="text-primary text-sm leading-none font-medium text-nowrap">
									{title}
								</div>
							)}
							{children}
						</div>
					</Link>
				)}
			</NavigationMenuLink>
		</li>
	);
}
